/**
 * OCPI Authentication Method enum values
 */
export type OcpiAuthMethod = 'AUTH_REQUEST' | 'COMMAND' | 'WHITELIST' | 'UNKNOWN';

/**
 * OCPI Session State enum values
 */
export type SessionState = 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'INVALID' | 'UNKNOWN' | 'STARTED' | 'RESERVATION';

export const SESSION_STATUS_OPTIONS: SessionState[] = ['ACTIVE', 'COMPLETED', 'PENDING', 'STARTED', 'INVALID'];

export type SettlementFilter = 'all' | 'settled' | 'no-settlement';

/**
 * OCPI Connector Type enum values
 */
export type OcpiConnectorType =
  | 'CHADEMO' | 'CHAOJI' | 'DOMESTIC_A' | 'DOMESTIC_B' | 'DOMESTIC_C'
  | 'DOMESTIC_D' | 'DOMESTIC_E' | 'DOMESTIC_F' | 'DOMESTIC_G' | 'DOMESTIC_H'
  | 'DOMESTIC_I' | 'DOMESTIC_J' | 'DOMESTIC_K' | 'DOMESTIC_L' | 'DOMESTIC_M'
  | 'DOMESTIC_N' | 'DOMESTIC_O' | 'GBT_AC' | 'GBT_DC' | 'IEC_60309_2_single_16'
  | 'IEC_60309_2_three_16' | 'IEC_60309_2_three_32' | 'IEC_60309_2_three_64'
  | 'IEC_62196_T1' | 'IEC_62196_T1_COMBO' | 'IEC_62196_T2' | 'IEC_62196_T2_COMBO'
  | 'IEC_62196_T3A' | 'IEC_62196_T3C' | 'NEMA_5_20' | 'NEMA_6_30' | 'NEMA_6_50'
  | 'NEMA_10_30' | 'NEMA_10_50' | 'NEMA_14_30' | 'NEMA_14_50' | 'PANTOGRAPH_BOTTOM_UP'
  | 'PANTOGRAPH_TOP_DOWN' | 'TESLA_R' | 'TESLA_S' | 'UNKNOWN';

/**
 * OCPI Connector Format enum values
 */
export type OcpiConnectorFormat = 'SOCKET' | 'CABLE' | 'UNKNOWN';

/**
 * OCPI Power Type enum values
 */
export type OcpiPowerType = 'AC_1_PHASE' | 'AC_2_PHASE' | 'AC_2_PHASE_SPLIT' | 'AC_3_PHASE' | 'DC' | 'UNKNOWN';

/**
 * Kiosk Session data model based on HIVE-4167 requirements
 * Represents a complete charging session with payment and technical details
 * Updated with new API fields from ApiSessionInfo (HIVE-6390)
 */
export interface KioskSession {
  // Main grid columns (primary display fields)
  /** Unique session identifier (from API: id) */
  session_id: string;

  /** Session status: COMPLETED, ACTIVE, FAILED */
  status: string;

  /** Session start timestamp (ISO format from API: startTime) */
  start_time: string;

  /** Session end timestamp (ISO format from API: endTime) */
  stop_time: string;

  /** Total energy consumed in kWh (from API: energy) */
  total_energy: number;

  /** Final settlement amount (from API: cost) */
  settlement_value: number;

  // Detail blade fields (additional session information)
  /** Location unique identifier */
  location_id: string;

  /** Human-readable location name (e.g., "Arik Einshtein 3") */
  location_name: string;

  /** Session duration as formatted string */
  duration: string;

  /** Last 4 digits of the payment card */
  last_4_digits: number;

  /** Authorization timestamp */
  auth_time: string;

  /** Settlement completion timestamp */
  settlement_time: string;

  /** Physical machine identifier */
  machine_id: number;

  /** Electric Vehicle Supply Equipment identifier (from API: evseId) */
  evse_id: string;

  /** Connector identifier (from API: connectorId) */
  connector_id: string;

  /** Charge Detail Record identifier (from API: cdrId) */
  cdr_id: string;

  /** Connector type specification (e.g., "CCS2") */
  connector_type: string;

  /** Last updated timestamp */
  updatedDT: string;

  /** Tariff/pricing plan identifier */
  tariff_id: number;

  /** Tariff description (e.g., "off-peak") */
  description: string;

  /** Price per unit (e.g., 0.30) */
  price_components: number;

  /** Tariff validity start date */
  valid_from: string;

  /** Tariff validity end date */
  valid_to: string;

  /** Time-based restrictions (e.g., "17:00-19:00") */
  restrictions: string;

  // Payment-specific fields for detail blade
  /** Payment method used (e.g., "Credit Card") */
  payment_method?: string;

  /** Currency code (e.g., "ILS") */
  currency?: string;

  /** Payment amount */
  amount?: number;

  /** Authorization result (e.g., "APPROVED", "DECLINED") */
  authorization_result?: string;

  /** Credit card brand (e.g., "Visa", "Mastercard") */
  card_brand?: string;

  /** Last four digits of payment card (string format) */
  last_four_digits?: string;

  /** Session start time (string format for display) */
  session_start?: string;

  /** Session end time (string format for display) */
  session_end?: string;

  // ===== NEW FIELDS FROM API (HIVE-6390) =====

  // Authentication & Token Info
  /** Authentication token (RFID/app token) */
  token?: string;

  /** Token type (RFID, APP_USER, etc.) */
  token_type?: string;

  /** Authentication method (AUTH_REQUEST, COMMAND, WHITELIST) */
  auth_method?: OcpiAuthMethod;

  /** Internal session state */
  state?: SessionState;

  /** Session notes/remarks */
  remark?: string;

  /** Vehicle license plate */
  plate?: string;

  /** Driver phone number as stored (incl. country prefix) */
  phone_number?: string;

  // Payment & Receipt Info
  /** Payment identifier */
  payment_id?: string;

  /** Receipt reference */
  receipt?: string;

  // Cost Breakdown
  /** Fixed/flat fee */
  fixed_cost?: number;

  /** Energy cost component */
  energy_cost?: number;

  /** Time-based cost component */
  time_cost?: number;

  /** Parking fee component */
  parking_cost?: number;

  /** Reservation fee component */
  reservation_cost?: number;

  // Connector Details
  /** OCPI connector standard (CCS2, Type2, CHAdeMO, etc.) */
  connector_standard?: OcpiConnectorType;

  /** Connector format (SOCKET or CABLE) */
  connector_format?: OcpiConnectorFormat;

  /** Maximum power capacity in kW */
  max_power?: number;

  /** Connector display name/alias */
  connector_alias?: string;

  /** Power type (AC_1_PHASE, AC_3_PHASE, DC) */
  power_type?: OcpiPowerType;

  // Time Details
  /** Total session time in hours */
  total_time?: number;

  /** Total parking time in hours */
  total_parking_time?: number;

  // Location Details
  /** Street address */
  address?: string;

  /** City */
  city?: string;

  /** Country */
  country?: string;

  /** Postal code */
  postal_code?: string;

  /** GPS latitude */
  latitude?: string;

  /** GPS longitude */
  longitude?: string;

  /** Location timezone */
  timezone?: string;

  /** OCPI country code */
  country_code?: string;

  /** OCPI party ID */
  party_id?: string;

  // Identifiers
  /** Operator ID */
  operator_id?: number;

  /** Transaction ID */
  transaction_id?: number;

  /** Site ID */
  site_id?: number;

  // ===== NEW PAYMENT TRANSACTION FIELDS (HIVE-6397) =====

  // Critical Payment Transaction Fields
  /** Authorization amount from Payment Engine */
  authAmount?: number;

  /** Settlement amount from Payment Engine (actual charged amount) */
  settAmount?: number;

  /** Authorization timestamp (ISO format) */
  authTime?: string;

  /** Settlement timestamp (ISO format) */
  settTime?: string;

  // VAT Breakdown (Calculated Charging Cost)
  /** Total charging cost including VAT (from tariff calculation) */
  totalIncVat?: number;

  /** Total charging cost excluding VAT */
  totalExcVat?: number;

  /** VAT amount on charging */
  vatAmount?: number;

  // Payment Details
  /** Card identifier - last 4 digits or PayPal account ID */
  cardIdentifier?: string;

  // Machine Name (may already exist in some contexts)
  /** Human-readable machine/kiosk name */
  machineName?: string;
}

/**
 * Filter parameters for kiosk session queries
 * Maps to API query parameters and UI filter options
 */
export interface KioskSessionFilters {
  /** Operator ID filter (required for API calls) */
  operatorId?: string;
  
  /** Location filter (optional, for location dropdown) */
  locationId?: string;

  /** Sub-operator actor ID filter (server-side; undefined = all sub-operators) */
  subOperatorId?: string;

  /** Date range start (YYYY-MM-DD format - ISO 8601) */
  dateFrom: string;
  
  /** Date range end (YYYY-MM-DD format - ISO 8601) */
  dateTo: string;
  
  /** Last 4 digits of credit card for payment filtering */
  last4Digits?: string;
  
  /** General search term across multiple fields */
  searchTerm?: string;

  /** Group sessions by location toggle */
  groupByLocation?: boolean;

  /** Optional limit for pagination (number of sessions per page) */
  limit?: number;

  /** Optional skip for pagination (number of sessions to skip, calculated as currentPage * pageSize) */
  skip?: number;

  /** Server-side transaction ID filter (fallback when client-side yields no results) */
  transactionId?: number;

  /** Server-side session ID filter (fallback when client-side yields no results) */
  sessionId?: string;

  /** Settlement filter: 'all' (default), 'settled' (settleAmount > 0), 'no-settlement' (null or <= 0) */
  settlement?: SettlementFilter;

  /** Filter by one or more session statuses (server-side) */
  statuses?: string[];
}

/**
 * Simplified session display model for grid presentation
 */
export interface KioskSessionGridRow {
  session_id: string;
  status: string;
  evse_id: string;
  connector_id: string;
  cdr_id: string;
  start_time: Date;
  stop_time: Date;
  total_energy: number;
  settlement_value: number;
  /** Last 4 digits of the payment card */
  last_4_digits: number;
  /** Location hierarchy path for grouping (e.g., "Israel/Tel Aviv/Station A") */
  actor_hierarchy?: string;
  /** Human-readable machine name */
  machine_name?: string;
  /** Payment transaction identifier */
  payment_transaction_id?: string;

  // ===== NEW GRID FIELDS (HIVE-6390) =====
  /** Authentication method (AUTH_REQUEST, COMMAND, WHITELIST) */
  auth_method?: OcpiAuthMethod;
  /** OCPI connector standard (CCS2, Type2, CHAdeMO, etc.) */
  connector_standard?: string;
  /** Maximum power capacity in kW */
  max_power?: number;
  /** Power type (AC_1_PHASE, AC_3_PHASE, DC) */
  power_type?: OcpiPowerType;
  /** Location name for display */
  location_name?: string;
  /** Currency code (e.g., "EUR") */
  currency?: string;

  // ===== NEW PAYMENT TRANSACTION FIELDS FOR GRID (HIVE-6397) =====
  /** Authorization amount from Payment Engine */
  authAmount?: number;
  /** Settlement amount from Payment Engine */
  settAmount?: number;
  /** Authorization timestamp */
  authTime?: Date;
  /** Settlement timestamp */
  settTime?: Date;
  /** Total charging cost including VAT */
  totalIncVat?: number;
  /** Total charging cost excluding VAT */
  totalExcVat?: number;
  /** VAT amount */
  vatAmount?: number;
  /** Payment method (CC or PP) */
  paymentMethod?: string;
  /** Card identifier - last 4 digits or PayPal ID */
  cardIdentifier?: string;
  /** Connector display name/alias */
  connector_alias?: string;
  /** Receipt URL */
  receipt?: string;
  /** Vehicle license plate (as entered, empty when not collected) */
  plate?: string;
  /** Driver phone number (as stored, empty when not collected) */
  phone_number?: string;
}

/**
 * Session detail model for the detail blade
 */
export interface KioskSessionDetail extends KioskSession {
  // Computed fields for UI display
  formattedStartTime?: string;
  formattedStopTime?: string;
  formattedDuration?: string;
  formattedEnergy?: string;
  formattedValue?: string;
}

/**
 * Parent row for grouped view (location header)
 */
export interface LocationGroupRow {
  /** Unique identifier for the row */
  id: string;
  /** Row type discriminator */
  type: 'location';
  /** Location actor ID */
  locationId: string;
  /** Location display name */
  locationName: string;
  /** Number of sessions in this location */
  sessionCount: number;
  /** Total energy consumed in this location (kWh) */
  totalEnergy: number;
  /** Total revenue from this location (settlement_value) */
  totalRevenue: number;
  /** Total settlement amount from this location (settAmount) */
  totalSettlement: number;
  /** Child rows (actual session data) */
  subRows: KioskSessionGridRow[];
}

/**
 * Union type for hierarchical table rows (supports both parent and child rows)
 */
export type HierarchicalSessionRow = LocationGroupRow | (KioskSessionGridRow & { type?: 'session' });